/**
 * CORE-1477 — отправка multipart-формы, о которой видно, что она идёт.
 *
 * `fetch` не отдаёт прогресс тела запроса и не ставит границы ожиданию: на
 * телефоне подвисший апстрим держит публикацию бесконечно, и автор видит
 * спиннер без конца. XHR даёт `upload.onprogress`, а по нему — две границы:
 *
 *  • `stallTimeoutMs` — байты перестали двигаться. Ровно тот случай, когда
 *    мобильная сеть отвалилась молча: TCP ещё «жив», данные не идут.
 *  • `ackTimeoutMs` — байты ушли целиком, а ответа нет. Отдельная граница,
 *    потому что сервер здесь ещё и модерирует картинку: обрывать по общему
 *    таймауту значило бы рвать нормальную работу.
 *
 * Возвращает обычный `Response`, чтобы вызывающий разбирал его тем же
 * `handleResponse`, что и путь через `fetch` — включая 422 модерации.
 */

export interface FormUploadOptions {
  /** 0–100, на каждый тик XHR. */
  onProgress?: (percent: number) => void;
  signal?: AbortSignal;
  /** POST по умолчанию; правка поста уходит PUT. */
  method?: 'POST' | 'PUT';
  /** Ожидание ответа после того, как байты ушли. */
  ackTimeoutMs?: number;
  /** Терпение к отсутствию движения байтов. */
  stallTimeoutMs?: number;
}

const DEFAULT_ACK_TIMEOUT_MS = 90_000;
const DEFAULT_STALL_TIMEOUT_MS = 45_000;

/** Ошибка сети с тем же полем `status`, что у ApiError, — его читает friendlyApiError. */
function networkError(message: string): Error & { status?: number } {
  return Object.assign(new Error(message), { status: 0 });
}

/** `getAllResponseHeaders` отдаёт сырую строку; Response хочет пары. */
function parseHeaders(raw: string): Headers {
  const headers = new Headers();
  for (const line of (raw || '').trim().split(/[\r\n]+/)) {
    const i = line.indexOf(':');
    if (i <= 0) continue;
    try {
      headers.append(line.slice(0, i).trim(), line.slice(i + 1).trim());
    } catch {
      // Заголовок, который Headers не принимает, роли не играет.
    }
  }
  return headers;
}

export function uploadFormWithProgress(
  url: string,
  body: FormData,
  headers: Record<string, string>,
  opts: FormUploadOptions = {},
): Promise<Response> {
  const ackTimeoutMs = opts.ackTimeoutMs ?? DEFAULT_ACK_TIMEOUT_MS;
  const stallTimeoutMs = opts.stallTimeoutMs ?? DEFAULT_STALL_TIMEOUT_MS;

  return new Promise<Response>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let stallTimer: ReturnType<typeof setTimeout> | null = null;
    let ackTimer: ReturnType<typeof setTimeout> | null = null;
    let settled = false;

    const clearTimers = () => {
      if (stallTimer !== null) { clearTimeout(stallTimer); stallTimer = null; }
      if (ackTimer !== null) { clearTimeout(ackTimer); ackTimer = null; }
    };
    const fail = (message: string) => {
      if (settled) return;
      settled = true;
      clearTimers();
      try { xhr.abort(); } catch { /* уже закрыт */ }
      reject(networkError(message));
    };
    const armStall = () => {
      if (stallTimer !== null) clearTimeout(stallTimer);
      stallTimer = setTimeout(
        () => fail('Загрузка остановилась — проверьте связь и попробуйте ещё раз.'),
        stallTimeoutMs,
      );
    };
    // Байты ушли: дальше ждём ответ, а не движение.
    const armAck = () => {
      if (stallTimer !== null) { clearTimeout(stallTimer); stallTimer = null; }
      if (ackTimer !== null) return;
      ackTimer = setTimeout(
        () => fail('Сервер не ответил на публикацию — попробуйте ещё раз.'),
        ackTimeoutMs,
      );
    };

    const onAbort = () => fail('Публикация отменена.');
    opts.signal?.addEventListener('abort', onAbort);

    xhr.open(opts.method ?? 'POST', url);
    for (const [name, value] of Object.entries(headers)) xhr.setRequestHeader(name, value);

    xhr.upload.onprogress = (e) => {
      if (!e.lengthComputable) return;
      opts.onProgress?.(e.total > 0 ? Math.min(100, (e.loaded / e.total) * 100) : 0);
      // RN не обещает финальный тик с loaded === total и не зовёт onloadend
      // у upload, поэтому граница переставляется прямо здесь.
      if (e.loaded >= e.total) armAck();
      else armStall();
    };
    xhr.upload.onloadend = () => armAck();

    xhr.onload = () => {
      if (settled) return;
      settled = true;
      clearTimers();
      opts.signal?.removeEventListener('abort', onAbort);
      opts.onProgress?.(100);
      resolve(new Response(xhr.responseText, {
        status: xhr.status,
        statusText: xhr.statusText,
        headers: parseHeaders(xhr.getAllResponseHeaders?.() ?? ''),
      }));
    };
    xhr.onerror = () => fail('Не удалось отправить публикацию — проверьте связь.');
    xhr.ontimeout = () => fail('Публикация не уложилась во время ожидания — попробуйте ещё раз.');

    armStall();
    xhr.send(body as unknown as Document);
  });
}
